/**
 * A verdict, and the criteria that produced it (spec: surface expectations,
 * Task 13).
 *
 * The chip OPENS. A chip that says FAIL and nothing else starts an argument
 * with the contractor; the criteria list settles it by showing which check
 * failed, by how much, and against what.
 *
 * Every severity carries a glyph as well as a colour. Colour alone fails for
 * a red-green colour deficiency and on a greyscale print — already this
 * codebase's stated rule (`server/background/src/functions/processReport/stationSectionSvg.ts:6`,
 * "hatched as well as coloured so a greyscale print still reads").
 *
 * `SEVERITY` is exported (not local) because Task 14's per-bin verdict cell
 * in the station-volumes table reuses the same glyph + colour map — a bin
 * amber in the table and red in this header would be worse than no colour
 * at all.
 *
 * TWIN: `VERDICT_GLYPH` in
 * `server/background/src/functions/processReport/types.ts`. This map is the
 * SINGLE SOURCE for the glyphs (owner ruling); that one is the PDF's copy,
 * separate only because the report worker must not import a React module,
 * and it carries one extra row (`PENDING`, a state rather than a severity).
 * The two disagreed on MARGINAL until the crossfall check printed the same
 * band's mark in the dock's section label and in the PDF's. Changing a glyph
 * here means changing it there; the report's `__tests__/types.test.ts` reads
 * THIS file as text and fails if they drift.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { mToMm } from '@/lib/surfaceExpectation';
import { cn } from '@/lib/utils';

export const SEVERITY = {
  PASS: { glyph: '✓', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' },
  MARGINAL: { glyph: '~', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-400' },
  FAIL: { glyph: '✗', cls: 'bg-red-500/15 text-red-700 dark:text-red-400' },
  INCONCLUSIVE: { glyph: '?', cls: 'bg-slate-500/15 text-slate-700 dark:text-slate-400' },
} as const;

export type Verdict = keyof typeof SEVERITY;

/**
 * The only four criterion keys `evaluate()` ever produces. `meanOffset`
 * (MATCH only) and `meanThickness` (CONSTANT/SURFACES only) are mutually
 * exclusive by mode; `outOfToleranceArea` and `coverage` are always present.
 * There is no `crossings` criterion — crossings are counted into
 * `outOfToleranceArea`'s budget, not judged on their own.
 */
export type CriterionKey = 'meanOffset' | 'meanThickness' | 'outOfToleranceArea' | 'coverage';

export interface Criterion {
  key: CriterionKey;
  pass: boolean;
  actual: number;
  required: number;
}

/**
 * `outOfToleranceArea` and `coverage` are already percentages of measured
 * area. `meanOffset` and `meanThickness` are metres on the wire — repo
 * convention: thicknesses are metres on the wire, millimetres on screen, so
 * they are converted here and never printed raw (e.g. `0.3875`).
 */
const PCT_KEYS = new Set(['outOfToleranceArea', 'coverage']);

const fmtCriterion = (key: string, v: number): string =>
  PCT_KEYS.has(key) ? `${v.toFixed(1)} %` : `${mToMm(v).toFixed(1)} mm`;

export function VerdictChip({ verdict, criteria }: { verdict: Verdict; criteria: Criterion[] }) {
  const { t } = useTranslation('project');
  const [open, setOpen] = useState(false);
  const s = SEVERITY[verdict];

  return (
    <div className='relative inline-block'>
      <button
        type='button'
        data-testid='verdict-chip'
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn('inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium', s.cls)}>
        <span aria-hidden='true'>{s.glyph}</span>
        {t(`expectation.verdict.${verdict}`)}
      </button>
      {open && (
        <ul
          data-testid='verdict-criteria'
          className='absolute z-10 mt-1 min-w-64 rounded border border-slate-200/70 bg-white p-2 text-xs shadow dark:border-stone-700 dark:bg-stone-900'>
          {criteria.map((c) => {
            const rowSeverity = c.pass ? SEVERITY.PASS : SEVERITY.FAIL;
            return (
              <li
                key={c.key}
                data-testid='verdict-criterion'
                data-pass={String(c.pass)}
                className={cn('flex items-center justify-between gap-3 rounded px-1 py-0.5', rowSeverity.cls)}>
                <span aria-hidden='true'>{rowSeverity.glyph}</span>
                {/* Colour is never the only cue (file header), and a glyph
                    hidden from assistive tech (`aria-hidden`) needs a text
                    equivalent — without this, a screen-reader user hears the
                    criterion name and both numbers but never learns whether
                    it passed, which is the one fact this row exists to
                    convey. Visually hidden, not visually redundant: the
                    row's own background/text colour already carries this for
                    sighted users. */}
                <span className='sr-only'>
                  {c.pass ? t('expectation.verdict.PASS') : t('expectation.verdict.FAIL')}
                </span>
                <span className='flex-1'>{t(`expectation.criterion.${c.key}`)}</span>
                <span className='tabular-nums'>
                  {fmtCriterion(c.key, c.actual)} / {fmtCriterion(c.key, c.required)}
                </span>
              </li>
            );
          })}
          {/* Design doc §6.3: v0 has no virtual coring, so the verdict cannot
              claim conformance with a core-based specification. Said here,
              where the verdict is read, not in a footnote nobody opens. */}
          <li className='mt-1 border-t border-slate-200/70 pt-1 text-slate-500 dark:border-stone-700 dark:text-stone-400'>
            {t('expectation.advisory')}
          </li>
        </ul>
      )}
    </div>
  );
}

/**
 * The verdict WITHOUT its criteria list: a static badge, for
 * `SurfaceDifferencePanel`'s collapsed header summary.
 *
 * Not `VerdictChip` with the popover suppressed — the summary is rendered
 * inside `DockPanel`'s collapse toggle, which is a `<button>`, and a button
 * inside a button is invalid HTML that browsers flatten unpredictably. So this
 * is a `<span>`, and it deliberately cannot be opened: expanding the card is
 * how you get to the criteria.
 *
 * Both the glyph and the colour come from the shared `SEVERITY` map, so the
 * badge a reader sees on a collapsed card and the chip they see when they
 * expand it can never disagree about which severity this is.
 */
export function VerdictBadge({ verdict }: { verdict: Verdict }) {
  const { t } = useTranslation('project');
  const s = SEVERITY[verdict];

  return (
    <span
      data-testid='verdict-badge'
      className={cn('ml-2 inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 font-medium', s.cls)}>
      <span aria-hidden='true'>{s.glyph}</span>
      {t(`expectation.verdict.${verdict}`)}
    </span>
  );
}
